import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpRight,
  BookOpen,
  Check,
  CheckCircle2,
  CircleDashed,
  Clipboard,
  FileDown,
  FileSearch,
  Landmark,
  LoaderCircle,
  Printer,
  ShieldAlert,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';

import type {
  CallReadyAnswer,
  CallReadyAnswerBasis,
  CallReadyAnswerStatus,
  CallReadyFindingsModel,
} from '../../../lib/findings/callReadyFindings';
import { CALL_READY_BASIS_LABEL } from '../../../lib/findings/callReadyLabels';
import { spokenWordCount, spokenSeconds } from '../../../lib/findings/spokenMeter';
import { countWordAtSentenceStart } from '../../../lib/findings/callReadyStage';
import { AnswerLines, RiskRegister, LeadFacts, ConfirmList, ValidationBlock, PresenterNotes, briefRouteLabel, type BriefRouteHandler } from './brief/InstrumentParts';
import { logAuditEvent } from '../../../lib/telemetry/auditTelemetry';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { StatusPill, type StatusPillVariant } from '../../ui/status-pill';
import type { EconomicContributionSiteInputs } from '../../../types/economicContribution';
import {
  EconomicScreeningControls,
  type EconomicScreeningControlsHandle,
} from '../economicContribution/EconomicScreeningControls';
import { buildEconomicContributionBriefModel } from '../../../lib/economicContribution/briefModel';
import { buildEconomicContributionBriefHtml } from '../../../lib/economicContribution/briefHtml';
import type { EconomicEvidenceResearchController } from './useEconomicEvidenceResearch';
import type { EconomicWorkforceProfileController } from './useEconomicWorkforceProfile';
import { WorkforceProfileSummary } from './WorkforceProfileSummary';
import { ComparableProjectsAnswer } from './ComparableProjectsAnswer';
import { buildComparableProjectsPresentation } from '../../../lib/findings/comparableProjectsPresentation';
import { AnswerRangeRail } from './AnswerRangeRail';
import { buildEconomicResearchResultSnapshot } from '../../../lib/economicContribution/researchResultSnapshot';

/** Tab names for the Brief's phase routes (see `phaseDestinationLabels`). */
export type PhaseDestinationLabels = Partial<Record<'build' | 'verify' | 'decide', string>>;

export interface CallReadyFindingsSummaryProps {
  model: CallReadyFindingsModel;
  onOpenAnalysis?: (analysisId: string) => void;
  onRunEconomicScreen?: (inputs: EconomicContributionSiteInputs) => Promise<void>;
  economicResearch?: EconomicEvidenceResearchController;
  economicWorkforceProfile?: EconomicWorkforceProfileController;
  /**
   * Open the work of a phase: `build` → Analyses, `verify` → the first
   * verification item (Site evidence), `decide` → the Decide surface. The
   * host routes it (Path to GO removal, stage 2 — this used to open the hub).
   */
  onOpenPhase?: (
    phase: 'build' | 'verify' | 'decide',
    context?: { questionId: CallReadyAnswer['id']; questionLabel: string },
  ) => void;
  /**
   * The tab each `onOpenPhase` route opens, by name — the host resolves it from
   * the same router, so a question's "Continue in <tab>" names where the click
   * lands (a verify route can open Intent or the Site Map when that is where
   * the first open item lives). A missing phase reads "Continue".
   */
  onOpenRoute?: BriefRouteHandler;
  phaseDestinationLabels?: PhaseDestinationLabels;
  /** Number of required analyses that can be run or refreshed right now. */
  pathToGoAnalysisOpenCount?: number;
}

const STATUS_PRESENTATION: Record<CallReadyAnswerStatus, {
  label: string;
  variant: StatusPillVariant;
  icon: LucideIcon;
}> = {
  ready: { label: 'Ready', variant: 'success', icon: CheckCircle2 },
  partial: { label: 'Partial', variant: 'warn', icon: TriangleAlert },
  unavailable: { label: 'Open', variant: 'neutral', icon: CircleDashed },
  reference: { label: 'Reference', variant: 'custom', icon: BookOpen },
  'review-pending': { label: 'Copy policy blocked', variant: 'warn', icon: ShieldAlert },
};

function statusPresentationFor(
  answer: CallReadyAnswer,
  research?: EconomicEvidenceResearchController,
  workforce?: EconomicWorkforceProfileController,
): (typeof STATUS_PRESENTATION)[CallReadyAnswerStatus] {
  if (answer.id === 'comparables' && (research?.run?.resultSnapshot?.policyValidRecords.length ?? 0) > 0) {
    return STATUS_PRESENTATION.ready;
  }
  if (answer.id === 'workforce-wages') {
    if (workforce?.locationCheckPending || workforce?.loading) {
      return { label: 'Loading public data', variant: 'custom', icon: LoaderCircle };
    }
    if (workforce?.error && answer.status === 'unavailable') {
      return { label: 'Refresh failed', variant: 'danger', icon: TriangleAlert };
    }
  }
  if (answer.id === 'comparables' && answer.status !== 'ready' && research?.transportError && !research.run) {
    return { label: 'Refresh failed', variant: 'danger', icon: TriangleAlert };
  }
  // Operational research never demotes an already-usable answer. Its inline
  // strip can show refresh progress/failure while the answer pill stays Ready.
  if (answer.id === 'comparables' && answer.status !== 'ready' && research?.run) {
    const { run } = research;
    if (run.stage !== 'complete' && run.terminalResult === undefined) {
      return { label: 'Researching', variant: 'custom', icon: LoaderCircle };
    }
    if (run.terminalResult === 'complete-needs-context') {
      return { label: 'Needs context', variant: 'warn', icon: TriangleAlert };
    }
    if (run.terminalResult === 'complete-no-policy-valid-evidence') {
      return { label: 'No result', variant: 'neutral', icon: CircleDashed };
    }
    if (run.terminalResult === 'failed') {
      return { label: 'Research failed', variant: 'danger', icon: TriangleAlert };
    }
  }
  return STATUS_PRESENTATION[answer.status];
}

const BASIS_LABEL = CALL_READY_BASIS_LABEL;

const QUESTION_GROUPS: ReadonlyArray<{
  label: string;
  ids: readonly CallReadyAnswer['id'][];
}> = [
  { label: 'Power and cooling', ids: ['resources', 'power-cooling-risks'] },
  { label: 'Site and community', ids: ['infrastructure', 'community-concerns'] },
  {
    label: 'Jobs and economy',
    ids: ['permanent-jobs', 'construction-jobs', 'workforce-wages', 'workforce', 'staffing-model', 'comparables', 'fiscal'],
  },
];

/**
 * Answers that carry the economic screening form. The power & cooling lead
 * does not: its load and cooling come from the site record, and the form's
 * inputs are a what-if for the jobs and water screens.
 */
const SCREENING_INPUT_QUESTION_IDS = new Set<CallReadyAnswer['id']>([
  'permanent-jobs',
  'construction-jobs',
  'comparables',
  'fiscal',
]);

/**
 * ISO date-only forms (`YYYY`, `YYYY-MM`, `YYYY-MM-DD`). Per ECMA-262 these
 * parse as UTC midnight, whereas any form carrying a time parses as an instant.
 */
const DATE_ONLY_VINTAGE = /^\d{4}(?:-\d{2}(?:-\d{2})?)?$/;

/**
 * `CallReadyEvidenceSource.vintage` carries two different kinds of value:
 *  - calendar-only publication dates and reporting periods (`source.publicationDate`
 *    is `'2019-01-09'` / `'2024-12'` / `'2023'`; `section.dataAsOf`; `observation.period`)
 *  - real instants (`site.updatedAt`, `report.createdAt`, `economicSnapshot.generatedAt`)
 *
 * Date-only values must render in UTC or every viewer west of Greenwich reads the
 * previous day (`'2024-12'` displayed as "Nov 30, 2024"). Instants must keep
 * rendering in the viewer's own zone — that is the intended behaviour and forcing
 * UTC on them would be a new defect. Discriminate on the shape of the string,
 * which is exactly the predicate that decides how `Date.parse` anchored it.
 */
function formatEvidenceVintage(vintage: string): string {
  const timestamp = Date.parse(vintage);
  if (!Number.isFinite(timestamp)) return vintage;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(DATE_ONLY_VINTAGE.test(vintage) ? { timeZone: 'UTC' as const } : {}),
  }).format(new Date(timestamp));
}

export const CallReadyFindingsSummary: React.FC<CallReadyFindingsSummaryProps> = ({
  model,
  onOpenAnalysis,
  onRunEconomicScreen,
  economicResearch,
  economicWorkforceProfile,
  onOpenPhase,
  onOpenRoute,
  phaseDestinationLabels,
  pathToGoAnalysisOpenCount,
}) => {
  const [activeQuestionId, setActiveQuestionId] = useState(model.questions[0]?.id);
  const [copiedQuestionId, setCopiedQuestionId] = useState<CallReadyAnswer['id'] | null>(null);
  const [briefExporting, setBriefExporting] = useState<'docx' | 'pdf' | null>(null);
  const [briefExportError, setBriefExportError] = useState<string | null>(null);
  const economicControlsRef = useRef<EconomicScreeningControlsHandle>(null);
  const activeHeadingRef = useRef<HTMLHeadingElement>(null);
  const currentResearchRun = economicResearch?.run?.siteId === model.siteId
    ? economicResearch.run
    : null;
  const baselineResearchSnapshot = useMemo(() => {
    const snapshot = model.economicSnapshot;
    if (!snapshot || snapshot.comparableMatches.length === 0) return undefined;
    return buildEconomicResearchResultSnapshot({
      siteFingerprint: snapshot.inputHash,
      screening: snapshot,
      generatedAt: snapshot.generatedAt,
    });
  }, [model.economicSnapshot]);
  const siteEconomicResearch = economicResearch == null
    ? undefined
    : { ...economicResearch, run: currentResearchRun };
  const visibleQuestions = model.questions;
  const activeAnswer = visibleQuestions.find((question) => question.id === activeQuestionId)
    ?? visibleQuestions[0]
    ?? null;

  useEffect(() => {
    if (model.recordState === 'loading') return;
    logAuditEvent({
      type: 'findings-answer-sheet-viewed',
      siteId: model.siteId,
      payload: {
        questionCount: model.questions.length,
        readyCount: model.readyCount,
        referenceCount: model.referenceCount,
        copyVersion: model.copyGovernance.version,
        copyApproved: model.copyApproved,
      },
    });
  }, [model.copyApproved, model.copyGovernance.version, model.questions.length, model.readyCount, model.recordState, model.referenceCount, model.siteId]);

  const focusQuestion = (question: CallReadyAnswer): void => {
    setActiveQuestionId(question.id);
    logAuditEvent({
      type: 'findings-question-opened',
      siteId: model.siteId,
      payload: { questionId: question.id, status: question.status },
    });
    // On a one-column viewport the answer sits below the chooser. Move focus
    // there after React swaps the content so selecting a question does not
    // leave keyboard and screen-reader users at the top of a long index.
    window.setTimeout(() => {
      if (window.matchMedia?.('(max-width: 1279px)').matches) {
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
        activeHeadingRef.current?.focus({ preventScroll: true });
        activeHeadingRef.current?.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }, 0);
  };

  // Brief-level next step: analysis work still open → Analyses; nothing left
  // to run → the decision.
  const nextStepPhase = pathToGoAnalysisOpenCount === 0 ? 'decide' : 'build';
  const nextStepLabel = pathToGoAnalysisOpenCount == null
    ? 'Open Analyses'
    : pathToGoAnalysisOpenCount > 0
      ? 'Continue in Analyses'
      : 'Review the decision';
  const siteAnswers = model.questions.filter((question) => question.id !== 'answer-basis');
  const partialAnswerCount = siteAnswers.filter((question) => question.status === 'partial').length;
  const needsInputCount = Math.max(0, model.siteQuestionCount - model.readyCount - partialAnswerCount);
  const activeQuestionPosition = activeAnswer
    ? model.questions.findIndex((question) => question.id === activeAnswer.id) + 1
    : 0;

  const copyAnswer = async (answer: CallReadyAnswer, text?: string): Promise<void> => {
    if (!navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(text ?? answer.spokenSummary ?? answer.summary);
      setCopiedQuestionId(answer.id);
      window.setTimeout(() => setCopiedQuestionId(current => current === answer.id ? null : current), 1800);
    } catch {
      setCopiedQuestionId(null);
    }
  };

  const researchSnapshot = currentResearchRun?.resultSnapshot;
  // The exporters are client-bundled (DOCX package + browser print HTML), so
  // there is no separate deployment capability to probe. The authoritative
  // availability signal is whether this site has a source snapshot that the
  // brief model is allowed to export. Keep unavailable actions out of the
  // Stakeholder Brief instead of presenting disabled, non-actionable chrome.
  const briefReady = model.economicSnapshotAuthority === 'server-authored'
    || (researchSnapshot?.policyValidRecords.length ?? 0) > 0;
  const filenameBase = `${model.siteName}-economic-contribution-brief`
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  const exportBrief = async (format: 'docx' | 'pdf'): Promise<void> => {
    setBriefExportError(null);
    setBriefExporting(format);
    try {
      const brief = buildEconomicContributionBriefModel(
        model,
        { kind: 'internal-screening' },
        researchSnapshot,
      );
      if (format === 'docx') {
        const { buildEconomicContributionBriefDocx } = await import('../../../lib/economicContribution/briefDocx');
        const bytes = await buildEconomicContributionBriefDocx(brief);
        const url = URL.createObjectURL(new Blob([bytes as BlobPart], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filenameBase}.docx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        // Do not pass noopener/noreferrer: per HTML spec, window.open with
        // noopener returns null even when the tab opens, which prevents writing
        // the print HTML into the new document.
        const printWindow = window.open('', '_blank');
        if (!printWindow) throw new Error('Allow pop-ups to print the economic brief.');
        printWindow.document.open();
        printWindow.document.write(buildEconomicContributionBriefHtml(brief));
        printWindow.document.close();
        printWindow.focus();
        printWindow.setTimeout(() => printWindow.print(), 150);
      }
      logAuditEvent({
        type: 'economic-contribution-brief-created',
        siteId: model.siteId,
        payload: {
          format,
          snapshotId: brief.snapshotId,
          inputHash: brief.inputHash,
          authoritySnapshots: brief.authoritySnapshots,
        },
      });
    } catch (error) {
      setBriefExportError(error instanceof Error ? error.message : 'The economic brief could not be created.');
    } finally {
      setBriefExporting(null);
    }
  };

  if (model.recordState === 'loading') {
    return (
      <section
        className="py-8 font-sans"
        data-testid="call-ready-findings-summary"
        data-record-state="loading"
        aria-busy="true"
      >
        <div className="border-y border-border-subtle py-10 text-center" role="status">
          <LoaderCircle className="mx-auto h-5 w-5 animate-spin text-primary motion-reduce:animate-none" aria-hidden />
          <h2 className="mt-4 font-sans text-xl font-semibold text-text-primary">Loading stakeholder brief</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-text-muted">
            Verifying the current site record and analysis revisions. Answer status and reviewed wording will appear when that check is complete.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-5 font-sans" data-testid="call-ready-findings-summary" data-record-state="settled" data-brief-stage={model.stage?.briefStage} data-screening-complete={String(model.stage?.screeningComplete ?? false)}>
      <header className="border-b border-border-subtle pb-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="max-w-[62ch]">
            <h2 className="text-xl font-semibold text-text-primary">Stakeholder brief</h2>
            <p className="mt-1 max-w-prose text-sm text-text-secondary">Choose a question, review its answer, then open the next step if more information is needed.</p>
            <p className="mt-3 text-xs font-medium text-text-secondary">{model.stage?.label ?? 'Stage 1 · Screening'}</p>
            <p className="mt-2 text-sm leading-relaxed text-text-secondary" data-testid="call-ready-stage-context">{model.stage?.contextLine ?? 'Answers come from site evidence and project intent; the Screening Assessment adds to them.'}</p>
            <p
              className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-text-muted"
              data-testid="call-ready-summary-line"
            >
              <span><strong className="font-semibold tabular-nums text-text-primary">{model.readyCount}</strong> of {model.siteQuestionCount} ready</span>
              {partialAnswerCount > 0 ? <span>{partialAnswerCount} partial</span> : null}
              {needsInputCount > 0 ? <span>{needsInputCount} open</span> : null}
              {model.evidenceSourceCount > 0 ? <span>{model.evidenceSourceCount} {model.evidenceSourceCount === 1 ? 'source' : 'sources'}</span> : null}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {briefReady ? (
              <div className="flex flex-wrap items-center gap-1" data-testid="economic-brief-export-actions">
                <Button type="button" size="sm" variant="ghost" disabled={briefExporting != null} onClick={() => void exportBrief('docx')}>
                  <FileDown aria-hidden /> Download DOCX
                </Button>
                <Button type="button" size="sm" variant="ghost" disabled={briefExporting != null} onClick={() => void exportBrief('pdf')}>
                  <Printer aria-hidden /> Print / PDF
                </Button>
              </div>
            ) : null}
            {onOpenPhase ? (
              <Button type="button" size="sm" variant="outline" onClick={() => onOpenPhase(nextStepPhase)}>
                {nextStepLabel} <ArrowUpRight aria-hidden />
              </Button>
            ) : null}
          </div>
        </div>

        {briefExportError ? <p role="alert" className="mt-2 text-xs text-danger">{briefExportError}</p> : null}

        {!model.copyApproved ? (
          <div className="mt-4 flex items-start gap-2 border-l-2 border-warning bg-warning/5 px-3 py-2" data-testid="call-ready-copy-blocked">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
            <p className="text-xs leading-relaxed text-text-secondary">
              <span className="font-semibold text-text-primary">Internal draft:</span> answer wording is withheld until the versioned copy checks pass.
            </p>
          </div>
        ) : null}
        {model.stage?.basis.length ? <details className="mt-3 text-xs text-text-muted"><summary className="min-h-8 w-fit cursor-pointer py-1 font-medium">Source basis</summary><p className="mt-1 max-w-prose break-words" data-testid="call-ready-basis-line">{model.stage.basis.map(item => `${item.label}${item.value ? ` · ${item.value}` : ''}`).join(' / ')}</p></details> : null}
      </header>

      <div className="mt-6 grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3.5 xl:grid-cols-[16.5rem_minmax(0,1fr)]" data-testid="call-ready-question-layout">
        <nav
          className="min-w-0 xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto"
          aria-label="Stakeholder question index"
          data-testid="call-ready-question-index"
        >
          <label className="block xl:hidden">
            <span className="mb-1.5 block font-mono type-meta text-text-muted">
              Choose a stakeholder question
            </span>
            <select
              aria-label="Choose stakeholder question"
              value={activeAnswer?.id ?? ''}
              onChange={(event) => {
                const question = model.questions.find((candidate) => candidate.id === event.target.value);
                if (question) focusQuestion(question);
              }}
              className="min-h-11 w-full rounded-md border border-border bg-background-elevated px-3 py-2 text-sm font-semibold text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {QUESTION_GROUPS.map(group => <optgroup key={group.label} label={group.label}>{model.questions.filter(question => group.ids.includes(question.id)).map(question => <option key={question.id} value={question.id}>{String(model.questions.findIndex(item => item.id === question.id) + 1).padStart(2, '0')} · {question.shortLabel} · {statusPresentationFor(question, siteEconomicResearch, economicWorkforceProfile).label}</option>)}</optgroup>)}
            </select>
          </label>

          <div className="hidden xl:block">
            <div className="space-y-5">
            {QUESTION_GROUPS.map((group) => {
              const groupedQuestions = visibleQuestions.filter((question) => group.ids.includes(question.id));
              if (groupedQuestions.length === 0) return null;
              return (
                <section key={group.label} aria-label={group.label}>
                  <h3 className="mb-1.5 font-mono type-meta text-text-muted">{group.label}</h3>
                  <div className="border-l border-border-subtle">
                    {groupedQuestions.map((question) => {
                      const presentation = statusPresentationFor(question, siteEconomicResearch, economicWorkforceProfile);
                      const Icon = presentation.icon;
                      const active = activeAnswer?.id === question.id;
                      const questionPosition = model.questions.findIndex((candidate) => candidate.id === question.id) + 1;
                      return (
                        <button
                          key={question.id}
                          type="button"
                          aria-current={active ? 'true' : undefined}
                          onClick={() => focusQuestion(question)}
                          className={cn(
                            '-ml-px grid w-full grid-cols-[1.5rem_minmax(0,1fr)] gap-x-2 border-l-2 px-3 py-2.5 text-left transition-colors motion-reduce:transition-none',
                            active
                              ? 'border-primary bg-primary/5 text-text-primary'
                              : 'border-transparent text-text-secondary hover:bg-state-hover hover:text-text-primary',
                          )}
                          data-testid={`call-ready-index-${question.id}`}
                          data-answer-status={question.status}
                        >
                          <span className="pt-px font-mono type-meta tabular-nums text-text-muted" aria-hidden>
                            {String(questionPosition).padStart(2, '0')}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-xs font-semibold leading-snug">{question.shortLabel}</span>
                            <span className="mt-1 inline-flex items-center gap-1 font-mono type-meta leading-none text-text-muted">
                              <Icon className="h-3 w-3" aria-hidden />
                              {presentation.label}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })}
            </div>
          </div>
        </nav>

        <div className="min-w-0">
          {activeAnswer ? (() => {
            const presentation = statusPresentationFor(activeAnswer, siteEconomicResearch, economicWorkforceProfile);
            const spokenCopy = model.copyApproved ? activeAnswer.spokenSummary : undefined;
            const liveComparableSnapshot = currentResearchRun?.terminalResult === 'complete-with-evidence'
              && (currentResearchRun.resultSnapshot?.policyValidRecords.length ?? 0) > 0
              ? currentResearchRun.resultSnapshot
              : undefined;
            const comparableSnapshot = activeAnswer.id === 'comparables'
              ? liveComparableSnapshot ?? baselineResearchSnapshot
              : undefined;
            const comparablePresentation = comparableSnapshot
              ? buildComparableProjectsPresentation(comparableSnapshot)
              : null;
            const researchBackedComparableSummary = comparablePresentation
              ? liveComparableSnapshot
                ? `${countWordAtSentenceStart(comparablePresentation.peerCount)} reviewed comparable ${comparablePresentation.peerCount === 1 ? 'project matches' : 'projects match'} this site. Coverage includes ${comparablePresentation.coverageLine}; choose a project below.`
                : `${countWordAtSentenceStart(comparablePresentation.peerCount)} reviewed comparable ${comparablePresentation.peerCount === 1 ? 'project is' : 'projects are'} available. Choose a project below; ranges require at least three compatible records.`
              : null;
            // Status is already visible beside the question. Show the direct
            // answer here so the reader does not have to parse a second status
            // sentence; keep the governed wrapper for copied call-ready text.
            const displayedSummary = researchBackedComparableSummary
              ?? (model.copyApproved ? activeAnswer.spokenSummary ?? activeAnswer.summary : undefined);
            const copiedSummary = displayedSummary ?? spokenCopy;
            const onRoute = onOpenRoute ? (route: NonNullable<CallReadyAnswer['route']>) => onOpenRoute(route, { questionId: activeAnswer.id, questionLabel: activeAnswer.shortLabel }) : undefined;
            const displayedBases: readonly CallReadyAnswerBasis[] = comparablePresentation
              ? [...new Set<CallReadyAnswerBasis>([
                  ...activeAnswer.bases.filter((basis) => basis !== 'unavailable'),
                  'public-record',
                ])]
              : activeAnswer.bases;
            return (
              <article
                id={`call-ready-${model.siteId}-${activeAnswer.id}`}
                className="rounded-2xl border border-border-subtle bg-card p-5 shadow-sm sm:p-6"
                aria-label={`${activeAnswer.shortLabel} answer`}
                data-testid={`call-ready-answer-${activeAnswer.id}`}
              >
                <div className="-mx-5 -mt-5 mb-3 flex flex-wrap items-center gap-2 rounded-t-2xl border-b border-primary/40 bg-surface-2 px-5 py-3 sm:-mx-6 sm:-mt-6 sm:px-6">
                  <StatusPill variant={presentation.variant} icon={presentation.icon}>{presentation.label}</StatusPill>
                  <p className="font-mono type-meta text-text-muted">{activeAnswer.shortLabel}</p>
                  <p className="ml-auto font-mono type-meta tabular-nums text-text-muted" aria-label={`Question ${activeQuestionPosition} of ${model.questions.length}`}>
                    {String(activeQuestionPosition).padStart(2, '0')} / {String(model.questions.length).padStart(2, '0')}
                  </p>
                </div>
                <h3
                  ref={activeHeadingRef}
                  tabIndex={-1}
                  className="mt-3 max-w-[48rem] scroll-mt-16 font-sans text-[22px] font-semibold leading-[1.25] tracking-[-0.02em] text-text-primary outline-none"
                >
                  {activeAnswer.question}
                </h3>

                <div className="mt-5 border-l-2 border-primary bg-primary/5 px-4 py-4 sm:px-5 sm:py-5" data-testid="call-ready-spoken-answer">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="font-mono type-meta text-primary">Current answer</p>
                    {copiedSummary ? (
                      <Button type="button" size="sm" variant="ghost" onClick={() => void copyAnswer(activeAnswer, copiedSummary)} aria-live="polite">
                        {copiedQuestionId === activeAnswer.id ? <Check aria-hidden /> : <Clipboard aria-hidden />}
                        {copiedQuestionId === activeAnswer.id ? 'Copied' : 'Copy'}
                      </Button>
                    ) : null}
                  </div>
                  <p className="mt-2 max-w-[66ch] text-xl leading-[1.6] text-text-primary">{displayedSummary ?? 'Summary withheld until the versioned copy policy passes.'}</p>
                  {copiedSummary ? <p className="mt-3 font-mono type-meta text-text-muted" data-testid="call-ready-spoken-meter">{spokenWordCount(copiedSummary)} words · ~{spokenSeconds(copiedSummary)} seconds spoken</p> : null}
                </div>
                {activeAnswer.nextInput && !(activeAnswer.id === 'comparables' && siteEconomicResearch) ? (
                  <div className="mt-4 rounded-md bg-surface-2 p-4" data-testid="call-ready-next-step">
                    <p className="font-mono type-meta text-text-muted">
                      {activeAnswer.id === 'fiscal' ? 'Add inputs to calculate' : 'Next step'}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-text-secondary">{activeAnswer.nextInput}</p>
                    {activeAnswer.id === 'fiscal' && onRunEconomicScreen ? (
                      <div className="mt-3">
                        <Button
                          type="button"
                          size="sm"
                          aria-controls="economic-screening-controls"
                          onClick={() => economicControlsRef.current?.openFiscalScenario()}
                        >
                          <Landmark aria-hidden />
                          Enter tax / PILOT assumptions
                        </Button>
                      </div>
                    ) : activeAnswer.route && (onRoute || (activeAnswer.route.kind === 'phase' && onOpenPhase)) ? (
                      <div className="mt-3"><Button type="button" size="sm" variant="outline" className="border-primary/40" onClick={() => {
                        if (onRoute && activeAnswer.route) onRoute(activeAnswer.route);
                        else if (activeAnswer.route?.kind === 'phase') onOpenPhase?.(activeAnswer.route.phase, { questionId: activeAnswer.id, questionLabel: activeAnswer.shortLabel });
                      }}>{activeAnswer.route.kind === 'phase' && phaseDestinationLabels?.[activeAnswer.route.phase] ? `Continue in ${phaseDestinationLabels[activeAnswer.route.phase]}` : briefRouteLabel(activeAnswer.route)} <ArrowUpRight aria-hidden /></Button></div>
                    ) : null}
                  </div>
                ) : null}

                <div className="mt-3.5 space-y-3.5">
                  {model.copyApproved && activeAnswer.lines?.length ? <AnswerLines answerId={activeAnswer.id} lines={activeAnswer.lines} balance={activeAnswer.lineBalance} onRoute={onRoute} /> : null}
                  {model.copyApproved && activeAnswer.risks?.length ? <RiskRegister answer={activeAnswer} onRoute={onRoute} /> : null}
                  {activeAnswer.leadFacts?.length ? <LeadFacts id={activeAnswer.id} facts={activeAnswer.leadFacts} /> : null}
                  {activeAnswer.confirm?.length ? <ConfirmList id={activeAnswer.id} label={activeAnswer.confirmLabel} items={activeAnswer.confirm} onRoute={onRoute} /> : null}
                  {activeAnswer.validation ? <ValidationBlock id={activeAnswer.id} validation={activeAnswer.validation} /> : null}
                  {activeAnswer.expandsWith && model.stage?.briefStage !== 'mature-validation' ? <p className="type-caption text-text-muted" data-testid={`call-ready-expands-with-${activeAnswer.id}`}><span className="font-semibold">Expands with:</span> {activeAnswer.expandsWith}</p> : null}
                </div>

                {activeAnswer.visuals && activeAnswer.visuals.length > 0 ? (() => {
                  const primaryVisuals = activeAnswer.visuals.filter(visual => visual.presentation == null || visual.presentation === 'primary');
                  const contextVisuals = activeAnswer.visuals.filter(visual => visual.presentation === 'context');
                  const screenedOutVisuals = activeAnswer.visuals.filter(visual => visual.presentation === 'screened-out');
                  const supporting = activeAnswer.supportingVisualsLabel;
                  const visuals = (
                    <div
                      className={supporting ? 'mt-3' : 'mt-6'}
                      data-testid={`call-ready-visuals-${activeAnswer.id}`}
                      data-visual-layout={activeAnswer.id === 'resources' ? 'operating-mode-comparison' : 'range-summary'}
                    >
                      {primaryVisuals.length > 0 ? (
                        <div className={cn(
                          activeAnswer.id === 'resources'
                            ? 'space-y-5'
                            : 'grid gap-5 2xl:grid-cols-2',
                        )}>
                          {primaryVisuals.map((visual) => (
                            <AnswerRangeRail key={visual.id} visual={visual} />
                          ))}
                        </div>
                      ) : null}
                      {contextVisuals.length > 0 ? (
                        <details className="mt-3 border-y border-border-subtle bg-state-hover/20">
                          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 text-xs font-semibold text-text-secondary">
                            <span>Broad benchmark context</span>
                            <span className="font-mono type-meta font-normal uppercase tracking-[0.1em] text-text-muted">Not the selected design</span>
                          </summary>
                          <div className="grid gap-5 border-t border-border-subtle px-3 pb-2 2xl:grid-cols-2">
                            {contextVisuals.map((visual) => (
                              <AnswerRangeRail key={visual.id} visual={visual} />
                            ))}
                          </div>
                        </details>
                      ) : null}
                      {screenedOutVisuals.map((visual) => (
                        <details
                          key={visual.id}
                          className="mt-3 border-y border-border-subtle"
                          data-testid={`call-ready-screened-out-${visual.id}`}
                        >
                          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 text-xs font-semibold text-text-secondary">
                            <span>{visual.title}</span>
                            <span className="font-mono type-meta font-normal uppercase tracking-[0.1em] text-text-muted">Screened out for this site</span>
                          </summary>
                          <div className="border-t border-border-subtle px-3 pb-2">
                            {visual.reason ? (
                              <p className="mt-3 max-w-[72ch] text-xs leading-relaxed text-text-secondary">{visual.reason}</p>
                            ) : null}
                            <AnswerRangeRail visual={visual} />
                          </div>
                        </details>
                      ))}
                    </div>
                  );
                  // Supporting quantities (the power envelope and water bands
                  // behind a line-led answer) stay collapsed: they back the
                  // recommendation, they are not the answer.
                  return supporting ? (
                    <details
                      className="mt-6 border-y border-border-subtle"
                      data-testid={`call-ready-supporting-visuals-${activeAnswer.id}`}
                    >
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 text-xs font-semibold text-text-secondary">
                        <span>{supporting}</span>
                        <span className="font-mono type-meta font-normal uppercase tracking-[0.1em] text-text-muted">Screening quantities</span>
                      </summary>
                      <div className="border-t border-border-subtle px-3 pb-3">{visuals}</div>
                    </details>
                  ) : visuals;
                })() : null}

                {activeAnswer.id === 'comparables' && siteEconomicResearch ? (
                  <ComparableProjectsAnswer
                    research={siteEconomicResearch}
                    baselineSnapshot={baselineResearchSnapshot}
                  />
                ) : null}

                {activeAnswer.id === 'workforce-wages'
                  && economicWorkforceProfile
                  && (economicWorkforceProfile.locationCheckPending
                    || economicWorkforceProfile.loading
                    || economicWorkforceProfile.error) ? (
                  <div
                    className={cn(
                      'mt-4 border-l-2 px-3 py-2',
                      economicWorkforceProfile.error
                        ? 'border-danger bg-danger/5'
                        : 'border-primary bg-primary/5',
                    )}
                    role="status"
                    aria-live="polite"
                    data-testid="workforce-profile-load-status"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="text-xs leading-relaxed text-text-secondary">
                        {economicWorkforceProfile.error
                          ? economicWorkforceProfile.savedProfileUsableForDisplay
                            ? `Exact-coordinate refresh failed. The fresh saved profile for ${economicWorkforceProfile.profile?.countyName ?? 'the recorded county'} remains visible because it matches the server-authored screening county; retry to reverify the coordinates.`
                            : 'Public workforce data could not be refreshed. Any unverified prior-county profile remains hidden.'
                          : 'Loading federal workforce data from the site location. No input is required.'}
                      </p>
                      {economicWorkforceProfile.error ? (
                        <Button type="button" size="sm" variant="outline" onClick={() => void economicWorkforceProfile.refresh()}>
                          Refresh public data
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                <div className="mt-3.5"><PresenterNotes answer={activeAnswer} model={model} /></div>

                {model.economicContributionEnabled && SCREENING_INPUT_QUESTION_IDS.has(activeAnswer.id) ? (
                  <EconomicScreeningControls
                    ref={economicControlsRef}
                    inputs={model.economicInputs}
                    defaults={model.economicScreeningDefaults}
                    defaultLoadMw={model.economicDefaultLoadMw}
                    defaultLoadRange={model.economicDefaultLoadRange}
                    defaultLoadBasis={model.economicDefaultLoadBasis}
                    lastGeneratedAt={model.economicSnapshot?.generatedAt}
                    onRun={onRunEconomicScreen}
                  />
                ) : null}

                {activeAnswer.id === 'workforce-wages' && model.economicProfile ? (
                  <WorkforceProfileSummary profile={model.economicProfile} />
                ) : null}

                {activeAnswer.id !== 'comparables' ? <div className="mt-7 grid gap-x-6 gap-y-3 md:grid-cols-2">
                  {activeAnswer.details.length > 0 && activeAnswer.id !== 'workforce-wages' ? (
                    <details className="border-t border-border-subtle py-3">
                      <summary className="cursor-pointer list-none font-mono type-meta text-text-primary">
                        Details <span className="font-normal text-text-muted">{activeAnswer.details.length}</span>
                      </summary>
                      <dl className="mt-3 divide-y divide-border-subtle">
                        {activeAnswer.details.map((detail, index) => (
                          <div key={`${detail.label}:${index}`} className="py-2">
                            {detail.group && detail.group !== activeAnswer.details[index - 1]?.group ? <h4 className="mb-2 font-mono type-section text-text-secondary">{detail.group}</h4> : null}
                            <dt className="font-mono type-meta text-text-muted">{detail.label}</dt>
                            <dd className="mt-1 text-xs leading-relaxed text-text-primary">{detail.value}</dd>
                          </div>
                        ))}
                      </dl>
                    </details>
                  ) : null}

                  <details
                    className={cn(
                      'border-t border-border-subtle py-3',
                      (activeAnswer.id === 'workforce-wages' || activeAnswer.details.length === 0) && 'md:col-span-2',
                    )}
                    onToggle={(event) => {
                      if (!event.currentTarget.open) return;
                      logAuditEvent({
                        type: 'findings-evidence-opened',
                        siteId: model.siteId,
                        payload: { questionId: activeAnswer.id, sourceCount: activeAnswer.evidence.length },
                      });
                    }}
                  >
                    <summary className="flex cursor-pointer list-none items-center gap-2 font-mono type-meta text-text-primary">
                      <FileSearch className="h-3.5 w-3.5 text-primary" aria-hidden />
                      Evidence
                      <span className="font-normal text-text-muted">{activeAnswer.evidence.length}</span>
                    </summary>
                    <div className="mt-3 space-y-3">
                      <div className="border-l-2 border-primary/40 pl-3" aria-label="Answer basis">
                        <p className="font-mono type-meta text-text-muted">Answer basis</p>
                        <p className="mt-1 text-xs text-text-secondary">
                          {displayedBases.map((basis) => BASIS_LABEL[basis]).join(' · ')}
                        </p>
                      </div>
                      {activeAnswer.evidence.map((source) => (
                        <div key={source.id} className="border-t border-border-subtle pt-2 first:border-0 first:pt-0">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold text-text-primary">{source.label}</p>
                              {source.vintage ? <p className="mt-0.5 font-mono type-meta text-text-muted">Vintage {formatEvidenceVintage(source.vintage)}</p> : null}
                              {source.finding ? <p className="mt-1 text-xs leading-relaxed text-text-muted">{source.finding}</p> : null}
                              {source.excerpt ? <blockquote className="mt-1 border-l border-border-subtle pl-2 text-[11px] leading-relaxed text-text-secondary">“{source.excerpt}”{source.locator ? <cite className="mt-0.5 block not-italic text-[10px] text-text-muted">{source.locator}</cite> : null}</blockquote> : null}
                              {source.url ? <a href={source.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 font-mono type-meta text-primary hover:underline">Source <ArrowUpRight className="h-3 w-3" aria-hidden /></a> : null}
                            </div>
                            {source.reportId && onOpenAnalysis ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="shrink-0"
                                onClick={() => {
                                  logAuditEvent({
                                    type: 'findings-underlying-analysis-opened',
                                    siteId: model.siteId,
                                    reportId: source.reportId,
                                    reportType: source.reportType,
                                    payload: { questionId: activeAnswer.id },
                                  });
                                  onOpenAnalysis(source.reportId as string);
                                }}
                              >
                                Open <ArrowUpRight aria-hidden />
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                </div> : null}
                <nav className="mt-6 flex items-center justify-between border-t border-border-subtle pt-3" aria-label="Question pager">
                  <Button type="button" variant="ghost" size="sm" disabled={activeQuestionPosition <= 1} onClick={() => focusQuestion(model.questions[activeQuestionPosition - 2])}>← Previous</Button>
                  <span className="font-mono type-meta text-text-muted">{activeQuestionPosition} / {model.questions.length}</span>
                  <Button type="button" variant="ghost" size="sm" disabled={activeQuestionPosition >= model.questions.length} onClick={() => focusQuestion(model.questions[activeQuestionPosition])}>Next →</Button>
                </nav>
              </article>
            );
          })() : <p className="border-y border-border-subtle py-8 text-sm text-text-muted">No stakeholder questions are available.</p>}
        </div>
      </div>

      <details className="mt-8 border-t border-border-subtle pt-3" data-testid="call-ready-coverage">
        <summary className="cursor-pointer list-none font-mono type-meta text-text-muted">
          About this brief
        </summary>
        <div className="mt-3 grid gap-2 text-xs leading-relaxed text-text-muted sm:grid-cols-2">
          <p>
            {model.readyCount} ready
            {partialAnswerCount > 0 ? ` · ${partialAnswerCount} partial` : ''}
            {needsInputCount > 0 ? ` · ${needsInputCount} open` : ''}
          </p>
          <p>{model.evidenceSourceCount} source {model.evidenceSourceCount === 1 ? 'record' : 'records'}</p>
          {model.economicContributionEnabled ? (
            <p className="sm:col-span-2" data-testid="economic-screening-boundary">
              Early-stage screening, not a commitment. Review before external use.
            </p>
          ) : null}
        </div>
      </details>
    </section>
  );
};
